import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import { RootContext } from "../../../context/RootContext";
import { LoansContext } from "../../../context/LoansContext";

import { Clients } from "../../../types/enums";

import { useLoanSizingHandler } from "../../../hooks/useLoanSizingHandler";

import { getBorrowerFields } from "../LoanFields/LoanDetails/BorrowerInformation";
import { getPropertyLocationFields } from "../LoanFields/LoanDetails/PropertyLocationFields";
import { getPropertyInformationFields } from "../LoanFields/LoanDetails/PropertyInformationFields";
import { getPropertyEconomicsFields } from "../LoanFields/LoanDetails/PropertyEconomicsFields";
import { getLoanInformationFields } from "../LoanFields/LoanDetails/LoanInformation";
import { getLoanEconomicsFields } from "../LoanFields/LoanDetails/LoanEconomics";
import { getLoanCharacterizationFields } from "../LoanFields/LoanResults/LoanCharacterization";
import { getLoanFeaturesFields } from "../LoanFields/LoanResults/LoanFeatures";
import { getUnitInformationFields } from "../LoanFields/LoanDetails/UnitInformation";

import BorrowerDetails from "./BorrowerDetails";
import LoanDetails from "./LoanDetails";
import PropertyDetails from "./PropertyDetails";
import AutoCalculatedResults from "./AutoCalculatedResults";
import Documents from "./Documents";

const unitInformation: any = [{}];
const propertyDetails = [
  {
    propertyLocation: {},
    propertyInformation: {},
    propertyEconomics: {},
    unitInformation,
  },
];

export function DetailedLoanSizing(props: any) {
  const { t } = useTranslation();
  const [client, setClient] = useState<string>("");

  const { dynamicConfig } = useContext(RootContext);
  const {
    handleChange,
    onBlur,
    addressOrPostalOnInputChange,
    borrowerOnInputChange,
    guarantorOnInputChange
  } = useLoanSizingHandler();

  const { setDocumentList, documentList } = props;

  const {
    loanDetails,
    selectedSizingType,
    zipCodeOptions,
    addressOptions,
    detailedLoanSizingData,
    setDetailedLoanSizingData,
    fieldConfig,
    setFieldConfig,
    detailedLoanSizingErrors,
    autoCalculatedLoanResults,
    autoCalculatedPropResults,
    setCondoEligibility,
    borrowerOptions,
    guarantorOptions
  } = useContext(LoansContext);

  const handleAddProperty = () => {
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: [
        ...( detailedLoanSizingData?.propertyDetails || []),
        ...propertyDetails,
      ],
    });
  };

  const handleAddUnit = (propertyId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              unitInformation: [
                ...(property?.unitInformation || []),
                ...unitInformation,
              ],
            };
          }
          return property;
        }
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  const handleDeleteProperty = (propertyId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.filter(
        (property: any, index: number) => index !== propertyId
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  const handleDeleteUnit = (propertyId: number, unitId: number) => {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];
    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails?.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              unitInformation: property?.unitInformation?.filter(
                (unit: any, idx: number) => idx !== unitId
              ),
            };
          }
          return property;
        }
      );
    }
    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  };

  // Initialize field config
  useEffect(() => {
    const client =
      dynamicConfig.ViteAppKcRealm === "bplsizer"
        ? Clients?.BPL_SIZER
        : Clients?.MMTC;
    const loanTypeStr = loanDetails?.loanType?.includes("Ground") ? "GrUp" : "FixFlip";
    const sizingTypeForFields = loanDetails?.productType === "Bridge Loan" && client === "mmtc" ?  `${loanTypeStr}-${loanDetails?.propertyType}` : selectedSizingType; 

    setClient(client);
    setFieldConfig({
      borrowerDetails: {
        borrowerInformation: {
          fields: getBorrowerFields(
          loanDetails?.productType,
          client,
          "borrowerInformation",
          sizingTypeForFields
        ),
        header: "borrowerInformation",
        },
      },
      propertyDetails: {
        propertyLocation: {
          fields: getPropertyLocationFields(
            loanDetails?.productType,
            client,
            "propertyLocation",
            sizingTypeForFields
          ),
          header: "propertyLocation",
        },
        propertyInformation: {
          fields: getPropertyInformationFields(
            loanDetails?.productType,
            client,
            "propertyInformation",
            sizingTypeForFields
          ),
          header: "propertyInformation",
        },
        unitInformation: {
          fields: getUnitInformationFields(
            loanDetails?.productType,
            client,
            "unitInformation",
            sizingTypeForFields
          ),
          header: "unitInformation",
        },
        propertyEconomics: {
          fields: getPropertyEconomicsFields(
            loanDetails?.productType,
            client,
            "propertyEconomics",
            sizingTypeForFields
          ),
          header: "propertyEconomics",
        },
      },
      loanDetails: {
        loanInformation: {
          fields: getLoanInformationFields(
            loanDetails?.productType,
            client,
            "loanInformation",
            sizingTypeForFields
          ),
          header: "loanInformation",
        },
        loanEconomics: {
          fields: getLoanEconomicsFields(
            loanDetails?.productType,
            client,
            "loanEconomics",
            sizingTypeForFields
          ),
          header: "loanEconomics",
        },
      },
      autoCalculatedResults: {
        loanCharacterisation: {
          fields: getLoanCharacterizationFields(
            loanDetails?.productType,
            client,
            "loanCharacterisation",
            sizingTypeForFields
          ),
          header: "loanCharacterisation",
        },
        loanFeatures: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "loanFeatures",
            sizingTypeForFields,
            false
          ),
          header: "loanFeatures",
        },
        propLoanFeatures: {
            fields: getLoanFeaturesFields(
              loanDetails?.productType,
              client,
              "propLoanFeatures",
              sizingTypeForFields,
              true
            ),
            header: "propLoanFeatures",
        },
        marketData: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "marketData",
            sizingTypeForFields
          ),
          header: "marketData",
        },
        subMarketData: {
          fields: getLoanFeaturesFields(
            loanDetails?.productType,
            client,
            "subMarketData",
            sizingTypeForFields
          ),
          header: "subMarketData",
        },
      },
    });
  }, [
    dynamicConfig,
    loanDetails?.productType,
    selectedSizingType,
    setFieldConfig,
  ]);

  return (
    <div style={{ margin: "0px 16px" }}>
      <BorrowerDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        client={client}
        handleChange={handleChange}
        onBlur={onBlur}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        loanDetails={loanDetails}
        borrowerOnInputChange={borrowerOnInputChange}
        borrowerOptions={borrowerOptions}
        guarantorOnInputChange={guarantorOnInputChange}
        guarantorOptions={guarantorOptions}
      />
      <PropertyDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        client={client}
        handleChange={handleChange}
        onBlur={onBlur}
        loanDetails={loanDetails}
        handleAddUnit={handleAddUnit}
        handleAddProperty={handleAddProperty}
        handleDeleteUnit={handleDeleteUnit}
        handleDeleteProperty={handleDeleteProperty}
        zipCodeOptions={zipCodeOptions}
        addressOptions={addressOptions}
        addressOrPostalOnInputChange={addressOrPostalOnInputChange}
        allowEditAfterSubmit={dynamicConfig?.allowEditAfterSubmit}
        setDetailedLoanSizingData={setDetailedLoanSizingData}
        autoCalculatedPropResults={autoCalculatedPropResults}
      />
      <LoanDetails
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        detailedLoanSizingData={detailedLoanSizingData}
        client={client}
        detailedLoanSizingErrors={detailedLoanSizingErrors}
        handleChange={handleChange}
        onBlur={onBlur}
        loanDetails={loanDetails}
        setCondoEligibility={setCondoEligibility}
        setDetailedLoanSizingData={setDetailedLoanSizingData}
        results={{ autoCalculatedLoanResults, autoCalculatedPropResults }}
      />
      <AutoCalculatedResults
        fieldConfig={fieldConfig}
        isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
        data={{ autoCalculatedLoanResults, autoCalculatedPropResults }}
        client={client}
        loanDetails={loanDetails}
        t={t}
        handleChange={handleChange}
        onBlur={onBlur}
      />
      {dynamicConfig.showUploadDocumentBtn ? (
        <Documents
          isApplicationSubmitted={Boolean(loanDetails?.tcLoanId)}
          uploadDocument={props.uploadDocument}
          dynamicConfig={dynamicConfig}
          setDocumentList={setDocumentList}
          documentList={documentList}
        />
      ) : null}
    </div>
  );
}
